/**
 * <product-card-carousel>
 * Kave Home style product-card image carousel.
 *
 * - CSS scroll-snap track; no Swiper, no external dependency.
 * - Arrows are ALWAYS available and wrap: next on the last image goes to the
 *   first, prev on the first goes to the last (infinite loop both directions).
 * - A thin segmented indicator tracks position; the active segment is darker.
 * - Tiered image loading:
 *     TIER 1 (load): only slide 1 has a real src (1 request/card).
 *     TIER 2 (after `load`, requestIdleCallback): a page-level routine
 *       background-loads slide 2 (data-carousel-slide="1") for ALL carousels at
 *       fetchpriority="low".
 *     TIER 3 (per-card interaction): slides 3-5 (and slide 2 if not yet warm)
 *       hydrate on first interaction with THAT card (pointerenter / focusin /
 *       real swipe / arrow / wrap).
 * - Mobile arrow timing: arrows hidden on slide 1, shown from slide 2 via
 *   .is-engaged-mobile (latched at index >= 1). Desktop persistence latches
 *   .is-engaged at index >= 2.
 * - Swipe-hint nudge: mobile-only, once per carousel, reduced-motion-safe; a
 *   programmatic peek toward slide 2 that returns to slide 1 WITHOUT changing
 *   the index, latching engagement, or counting as a tier-3 interaction.
 * - Hover preview: when data-hover-advance="true" (driven by the "show second
 *   image on hover" setting), MOUSE hover advances to slide 2 (index 1). On
 *   mouse-leave, if still on slide 1 or 2 (index <= 1) it snaps back to slide 1
 *   and hides the controls; if the user has reached slide 3+ (index >= 2) leave
 *   does nothing. Never fires on touch.
 * - Persistence: .is-engaged latches only once the active index reaches >= 2
 *   (deliberate navigation to the 3rd image or beyond). Once latched, arrows +
 *   indicator stay visible off-hover for the rest of the session and leave no
 *   longer snaps back. Slides 1-2 stay an ephemeral hover preview.
 * - Works on dynamically inserted nodes via connectedCallback.
 */
if (!customElements.get('product-card-carousel')) {
  // Mobile swipe-hint travel distance (px). Tune here.
  const NUDGE_DISTANCE = 30;

  class ProductCardCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.count = parseInt(this.dataset.slideCount, 10) || this.slides.length;
      if (this.count <= 1) return;

      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.segments = Array.from(this.querySelectorAll('.product-card__carousel-indicator-segment'));
      this.current = 0;
      this.hydrated = false;
      this._nudging = false; // true while the swipe-hint nudge is animating
      // Desktop crossfade overlay (1<->2 hover + boundary crossfades).
      this.fade = this.querySelector('[data-carousel-fade]');
      this.fadeImg = this.fade && this.fade.querySelector('img');
      this.faded = false; // true while the soft hover-fade overlay is showing

      // Bound handlers (kept for disconnectedCallback cleanup).
      this._onPrev = (e) => this.go(e, -1);
      this._onNext = (e) => this.go(e, 1);
      this._syncDebounced = this.debounce(() => this.syncState(), 90);
      this._hydrateOnce = () => this.hydrate();
      this._onScroll = () => {
        // The programmatic swipe-hint nudge must NOT count as interaction.
        if (this._nudging) return;
        this.hydrate(); // a real user scroll (mobile swipe) hydrates tier-3
        this._syncDebounced();
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // First-interaction hydration for deferred slides (desktop pointer/focus).
      this.addEventListener('pointerenter', this._hydrateOnce, { once: true });
      this.addEventListener('focusin', this._hydrateOnce, { once: true });

      // Desktop hover soft-fade: crossfade image 2 OVER image 1 in place (no
      // track motion) when "show second image on hover" is on. Mouse only.
      this.hoverAdvance = this.dataset.hoverAdvance === 'true';
      this._onEnter = (e) => {
        if (e && e.pointerType === 'touch') return; // never on touch
        if (!this._isDesktop() || !this.hoverAdvance) return;
        if (this.current !== 0 || this.faded) return; // only from rest
        this.softFadeIn();
      };
      this.addEventListener('pointerenter', this._onEnter);

      // Mouse-leave while soft-faded: crossfade back to image 1 (no track move).
      this._onLeave = (e) => {
        if (e && e.pointerType === 'touch') return; // never on touch
        if (this.faded) this.softFadeOut();
      };
      this.addEventListener('pointerleave', this._onLeave);
    }

    _isDesktop() {
      return window.matchMedia('(min-width: 768px)').matches;
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
    }

    go(event, direction) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!this._isDesktop()) {
        // Mobile: native track paging with wrap (unchanged — no crossfade).
        const target = (this.current + direction + this.count) % this.count;
        this.goTo(target);
        return;
      }
      if (direction > 0) this._desktopNext();
      else this._desktopPrev();
    }

    // ---- Desktop crossfade-aware navigation -------------------------------
    // 1<->2 is a stacked crossfade (no track motion); 2<->3<->4<->5 is the
    // horizontal track. Image 2 is the pivot; its overlay and track-slide-1 are
    // the same image so handoffs never flash or double-move.
    _desktopNext() {
      if (this.faded) {
        // From the hover soft-fade (overlay img2 over track slide 0): snap the
        // track to slide 1 BEHIND the overlay (identical image, invisible),
        // drop the overlay instantly, then page horizontally to image 3.
        this._seamToTrack();
        this.goTo(2);
        return;
      }
      if (this.current === 0) {
        this._crossfadeTo(1); // image 1 -> image 2 (hover-advance off path)
        return;
      }
      if (this.current >= this.count - 1) {
        this._crossfadeTo(0); // wrap: last image -> image 1 (crossfade, no jump)
        return;
      }
      this.goTo(this.current + 1); // 2->3->4->5 horizontal track paging
    }

    _desktopPrev() {
      if (this.faded) {
        this.softFadeOut(); // image 2 (hover) -> image 1 (crossfade back)
        return;
      }
      if (this.current === 1) {
        this._crossfadeTo(0); // image 2 (track) -> image 1 (crossfade, no jump)
        return;
      }
      if (this.current === 0) {
        this._crossfadeTo(this.count - 1); // wrap: image 1 -> last (crossfade)
        return;
      }
      this.goTo(this.current - 1); // 5->4->3->2 horizontal track paging
    }

    goTo(target) {
      // Smooth horizontal track paging (used for images 2..5). Hydrates first.
      this.hydrate();
      this.track.scrollTo({ left: this._slideWidth() * target, behavior: 'smooth' });
      this.setActive(target); // optimistic; syncState() reconciles after settle
    }

    syncState() {
      if (this.faded) return; // don't reconcile from the track while soft-faded
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      this.setActive(Math.min(Math.max(index, 0), this.count - 1));
    }

    setActive(index) {
      if (index === this.current) return;
      this.current = index;
      // Mobile: arrows appear from slide 2 (index >= 1) and stay.
      if (this.current >= 1) this.classList.add('is-engaged-mobile');
      // Desktop: persist controls once the user reaches slide 3+ (index >= 2).
      if (this.current >= 2) this.classList.add('is-engaged');
      this.updateIndicator();
    }

    _slideWidth() {
      return this.track.clientWidth || (this.slides[0] && this.slides[0].clientWidth) || 0;
    }

    setTrackInstant(index) {
      // Force an INSTANT jump. The track has CSS `scroll-behavior: smooth`, so a
      // plain `scrollLeft = X` ANIMATES across the gap — and because the overlay is
      // cleared right after, that mid-animation (e.g. image 1 during the 0->1 hop)
      // flashes before the overlay's image. Temporarily override to `auto` so the
      // reposition lands synchronously, in the same frame, before the overlay clears.
      const prev = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = this._slideWidth() * index;
      this.track.style.scrollBehavior = prev;
    }

    _reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _fadeMs() {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue('--pcard-carousel-fade-speed')
        .trim();
      const n = parseInt(v, 10);
      return isFinite(n) && n > 0 ? n : 400;
    }

    // Copy slide[targetIndex]'s image into the overlay (works pre-hydration via
    // the data- attributes). Returns true if a src was applied.
    _applyOverlay(targetIndex) {
      const slide = this.slides[targetIndex];
      const img = slide && slide.querySelector('img');
      if (!this.fadeImg || !img) return false;
      const src = img.getAttribute('data-carousel-src') || img.currentSrc || img.src;
      const srcset = img.getAttribute('data-carousel-srcset') || img.getAttribute('srcset') || '';
      const sizes = img.getAttribute('data-carousel-sizes') || img.getAttribute('sizes') || '';
      if (sizes) this.fadeImg.sizes = sizes;
      if (srcset) this.fadeImg.srcset = srcset;
      if (src) this.fadeImg.src = src;
      return !!src;
    }

    // Hover soft-fade: image 2 over image 1, track stays at slide 0.
    softFadeIn() {
      if (!this.fade) return;
      this.hydrate(); // ensure image 2 (and the rest) load
      if (this._reducedMotion()) return; // no hover animation under reduced motion
      if (!this._applyOverlay(1)) return;
      this.faded = true;
      this.fade.style.transition = ''; // clear any 'none' left by a prior seam
      window.requestAnimationFrame(() => this.classList.add('is-fading'));
      this.setActive(1); // indicator -> image 2; track untouched
    }

    // Leave / prev from the soft-fade: crossfade overlay out -> image 1.
    softFadeOut() {
      if (!this.fade) return;
      this.fade.style.transition = '';
      this.classList.remove('is-fading'); // opacity 1 -> 0 reveals track slide 0
      this.faded = false;
      this.classList.remove('is-engaged');
      this.setActive(0);
    }

    // Hand the soft-fade off to the track at slide 1 with no visible change.
    _seamToTrack() {
      this.setTrackInstant(1);
      this.fade.style.transition = 'none'; // hide overlay with NO fade (identical image)
      this.classList.remove('is-fading');
      this.faded = false;
      void this.fade.offsetWidth; // commit, then restore transition for next time
      this.fade.style.transition = '';
      this.setActive(1);
    }

    // Hard crossfade the current view to slide[targetIndex] (used for the 1<->2
    // boundary via arrows and the wrap at the ends), then land on the track.
    _crossfadeTo(targetIndex) {
      if (!this.fade || !this._applyOverlay(targetIndex)) {
        this.goTo(targetIndex); // graceful fallback
        return;
      }
      this.hydrate();
      if (this._reducedMotion()) {
        // No animation: jump the track straight to the target.
        this.setTrackInstant(targetIndex);
        this.setActive(targetIndex);
        if (targetIndex === 0) this.classList.remove('is-engaged');
        return;
      }
      // Make sure we start from opacity 0 so the 0->1 transition fires.
      this.fade.style.transition = 'none';
      this.classList.remove('is-fading');
      void this.fade.offsetWidth;
      this.fade.style.transition = '';

      let done = false;
      const finish = (e) => {
        if (e && e.propertyName !== 'opacity') return;
        if (done) return;
        done = true;
        this.fade.removeEventListener('transitionend', finish);
        window.clearTimeout(timer);
        // Overlay fully opaque = target image. Snap track behind it, hide overlay.
        this.setTrackInstant(targetIndex);
        this.setActive(targetIndex);
        if (targetIndex === 0) this.classList.remove('is-engaged');
        this.fade.style.transition = 'none';
        this.classList.remove('is-fading');
        void this.fade.offsetWidth;
        this.fade.style.transition = '';
      };
      this.fade.addEventListener('transitionend', finish);
      const timer = window.setTimeout(finish, this._fadeMs() + 120); // safety net
      window.requestAnimationFrame(() => this.classList.add('is-fading'));
    }

    updateIndicator() {
      this.segments.forEach((segment, i) => {
        segment.classList.toggle('is-active', i === this.current);
      });
    }

    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;
      const deferred = this.querySelectorAll('img[data-carousel-src]');
      window.requestAnimationFrame(() => {
        deferred.forEach((img) => {
          const srcset = img.getAttribute('data-carousel-srcset');
          const sizes = img.getAttribute('data-carousel-sizes');
          const src = img.getAttribute('data-carousel-src');
          if (sizes) img.sizes = sizes;
          if (srcset) img.srcset = srcset;
          if (src) img.src = src;
          img.removeAttribute('data-carousel-src');
          img.removeAttribute('data-carousel-srcset');
          img.removeAttribute('data-carousel-sizes');
        });
      });
    }

    // Task 1: one-time mobile swipe-hint, TRANSFORM-based (GPU-composited, animates
    // identically on Chrome/Pixel/iOS). Translates the track left by NUDGE_DISTANCE
    // and back via a CSS transition — it never touches scrollLeft or scroll-snap,
    // so it cannot fire a scroll event, change the active index, hydrate tier-3, or
    // latch .is-engaged / .is-engaged-mobile. Mobile + motion-safe only.
    nudge() {
      if (this._nudged || !this.track || this.count <= 1) return;
      this._nudged = true;
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const track = this.track;
      // Settable hint duration from :root (--pcard-carousel-hint-speed), 320ms fallback.
      const hintSpeed =
        getComputedStyle(document.documentElement)
          .getPropertyValue('--pcard-carousel-hint-speed')
          .trim() || '320ms';
      let phase = 0; // 0 = travelling out, 1 = returning
      const onEnd = (e) => {
        if (e && e.propertyName !== 'transform') return;
        if (phase === 0) {
          phase = 1;
          track.style.transform = 'translateX(0px)'; // animate back to rest
        } else {
          track.removeEventListener('transitionend', onEnd);
          track.style.transition = '';
          track.style.transform = '';
        }
      };
      track.style.transition = 'transform ' + hintSpeed + ' ease';
      track.addEventListener('transitionend', onEnd);
      // Kick the out-translate on the next frame so the transition runs.
      window.requestAnimationFrame(() => {
        track.style.transform = 'translateX(-' + NUDGE_DISTANCE + 'px)';
      });
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('product-card-carousel', ProductCardCarousel);

  // --- Page-level, once-per-page routines (run after the `load` event) -------
  function pcCarouselPageInit() {
    // TIER 2: background-load slide 2 (data-carousel-slide="1") for every card,
    // low priority, during browser idle time.
    const tier2 = () => {
      const imgs = document.querySelectorAll(
        'product-card-carousel [data-carousel-slide="1"] img[data-carousel-src]'
      );
      imgs.forEach((img) => {
        const srcset = img.getAttribute('data-carousel-srcset');
        const sizes = img.getAttribute('data-carousel-sizes');
        const src = img.getAttribute('data-carousel-src');
        img.fetchPriority = 'low';
        if (sizes) img.sizes = sizes;
        if (srcset) img.srcset = srcset;
        if (src) img.src = src;
        img.removeAttribute('data-carousel-src');
        img.removeAttribute('data-carousel-srcset');
        img.removeAttribute('data-carousel-sizes');
      });
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(tier2, { timeout: 3000 });
    } else {
      window.setTimeout(tier2, 1200);
    }

    // TASK 2: mobile swipe-hint on the FIRST grid card only. The snippet emits
    // data-swipe-hint="true" on exactly one element (card_index == 1, setting ON,
    // >1 slide). nudge() itself re-checks mobile + reduced-motion.
    const firstCard = document.querySelector('product-card-carousel[data-swipe-hint="true"]');
    if (firstCard && typeof firstCard.nudge === 'function') {
      firstCard.nudge();
    }
  }

  function pcCarouselOnReady() {
    if (window.__pcCarouselPageInit) return;
    window.__pcCarouselPageInit = true;
    pcCarouselPageInit();
  }

  if (document.readyState === 'complete') {
    pcCarouselOnReady();
  } else {
    window.addEventListener('load', pcCarouselOnReady, { once: true });
  }
}
